import React, { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  X,
  FileText,
  Layers,
  ShieldCheck,
  ShieldAlert,
  Info,
  DollarSign,
  Calendar,
  Building,
  AlertCircle
} from 'lucide-react';

export interface CobValidationRuleResult {
  code: string;
  field: string;
  severity: 'VALID' | 'WARNING' | 'BLOCKING';
  message: string;
  value?: any;
}

export interface SecondaryCobPreviewData {
  serviceId: string;
  generatedAt: string;
  overallStatus: 'VALID' | 'BLOCKED';
  blockingCount: number;
  warningCount: number;
  validCount: number;
  sourceFacts: {
    serviceId: string;
    dos: string;
    serviceCode: string;
    modifier?: string;
    billableUnits: number;
    calculatedCharge: number;
    diagnosisCode: string;
    readinessStatus: string;
    clientId: string;
    clientName: string;
    dob?: string;
    gender?: string;
    address?: string;
    ahcccsId?: string;
    primaryPayerName?: string;
    primaryPayerId?: string;
    primaryPolicyId?: string;
    secondaryPayerName?: string;
    secondaryPayerId?: string;
    secondaryPolicyId?: string;
    payerType?: string;
    tplStatus?: string;
    primaryClaimId?: string;
    primaryClaimNumber?: string;
    primaryClaimRoute?: string;
    primaryClaimBilledAmount?: number;
    primaryClaimStatus?: string;
    primaryClaimCreatedAt?: string;
    authId?: string;
    authNumber?: string;
    providerId?: string;
    providerName?: string;
    providerNpi?: string;
    providerAhcccsId?: string;
    companyBillingName?: string;
    companyBillingNpi?: string;
    companyTaxId?: string;
    companyAhcccsId?: string;
    adjudicationId?: string;
    adjudicationDate?: string;
    adjudicationOutcome?: string;
    payerSequence?: string;
    billedAmount?: number;
    allowedAmount?: number;
    paidAmount?: number;
    contractualAdjustment?: number;
    patientResponsibility?: number;
    remainingSecondaryBalance?: number;
    groupCode?: string;
    carcCode?: string;
    rarcCode?: string;
    denialReason?: string;
    payerControlNumber?: string;
    tplEvidenceStatus?: string;
    canonicalEobId?: string;
    eobFilename?: string;
    eobReviewStatus?: string;
    eobReceivedDate?: string;
    paymentHeaderId?: string;
    checkEftNumber?: string;
    paymentMethod?: string;
    paymentDate?: string;
  };
  normalizedFacts: {
    primaryPayer: {
      name: string;
      electronicPayerId: string;
      resolvedElectronicId: string;
      payerResponsibilityCode: string;
      claimFilingIndicator: string;
    };
    otherSubscriber: {
      insuredLastName: string;
      insuredFirstName: string;
      primaryPolicyId: string;
      relationshipToInsured: string;
    };
    claimAdjudication: {
      paidAmount: number;
      allowedAmount: number;
      billedAmount: number;
      contractualAdjustment: number;
      patientResponsibility: number;
      remainingSecondaryAmount: number;
      adjudicationDate: string;
      adjudicationDateFormatted: string;
      payerControlNumber?: string;
      claimAdjustmentGroupCode: string;
      claimAdjustmentReasonCode: string;
      fullAdjustmentCode: string;
      rarcCode?: string;
      plainLanguageReason?: string;
      isQualifyingZeroDollarDenial: boolean;
    };
    secondarySubscriber: {
      secondaryPayerName: string;
      secondaryPayerId: string;
      memberAhcccsId: string;
      lastName: string;
      firstName: string;
      dob?: string;
      gender?: string;
      address?: string;
    };
    secondaryClaimHeader: {
      suggestedClaimNumber: string;
      claimBilledAmount: number;
      frequencyCode: string;
      placeOfService: string;
      authNumber?: string;
      diagnosisCode: string;
    };
    renderingProvider: {
      providerName: string;
      npi: string;
      ahcccsId: string;
    };
    lineCob: {
      lineItemNumber: number;
      serviceCode: string;
      modifier?: string;
      billedAmount: number;
      units: number;
      dos: string;
      primaryPaidAmount: number;
      primaryPayerId: string;
      lineAdjustmentGroupCode: string;
      lineAdjustmentReasonCode: string;
      lineAdjustmentAmount: number;
      lineAdjudicationDate: string;
    };
  } | null;
  validations: CobValidationRuleResult[];
  notice: string;
}

interface SecondaryCobPreviewModalProps {
  data: SecondaryCobPreviewData;
  onClose: () => void;
  onGenerate?: () => void;
  generating?: boolean;
  generateError?: string | null;
}

export const SecondaryCobPreviewModal: React.FC<SecondaryCobPreviewModalProps> = ({
  data,
  onClose,
  onGenerate,
  generating = false,
  generateError = null
}) => {
  const [activeTab, setActiveTab] = useState<'facts' | 'crosswalk' | 'validations'>('facts');
  const [filterSeverity, setFilterSeverity] = useState<'ALL' | 'BLOCKING' | 'WARNING' | 'VALID'>('ALL');

  const { sourceFacts: src, normalizedFacts: norm, validations } = data;

  const filteredValidations = validations.filter(v => {
    if (filterSeverity === 'ALL') return true;
    return v.severity === filterSeverity;
  });

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white border border-slate-200/90 rounded-2xl max-w-5xl w-full p-5 sm:p-6 space-y-4 shadow-2xl my-auto text-slate-800 max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-start border-b border-slate-200 pb-3.5 shrink-0">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-slate-100 text-slate-700 border border-slate-300">
                <Layers className="w-4 h-4" />
              </span>
              <h3 className="text-base font-bold text-slate-900 tracking-wide">
                Secondary COB Packet Preview & Validation
              </h3>
            </div>
            <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2">
              <span>Service: <strong className="text-slate-800">{src.dos}</strong></span>
              <span>•</span>
              <span>CPT: <strong className="text-slate-800">{src.serviceCode}{src.modifier ? `-${src.modifier}` : ''}</strong></span>
              <span>•</span>
              <span>Client: <strong className="text-slate-800">{src.clientName}</strong> ({src.clientId})</span>
              <span>•</span>
              <span>Readiness: <span className="font-mono text-slate-800 font-semibold">{src.readinessStatus}</span></span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
            title="Close Preview"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mandatory Visibility Notice */}
        <div className="p-3 rounded-xl bg-amber-50 border border-amber-300 flex items-center gap-3 shrink-0">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <div className="text-xs font-semibold text-amber-900 tracking-wide">
            Preview only. No 837 file has been created. No claim has been sent to WellSky.
          </div>
        </div>

        {/* Status Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200 shrink-0">
          <div className="flex items-center gap-2.5">
            {data.overallStatus === 'VALID' ? (
              <span className="px-3 py-1 rounded-md bg-emerald-100 text-emerald-800 border border-emerald-300 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>COB Packet Status: VALID</span>
              </span>
            ) : (
              <span className="px-3 py-1 rounded-md bg-rose-100 text-rose-800 border border-rose-300 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                <AlertCircle className="w-4 h-4 text-rose-600" />
                <span>COB Packet Status: BLOCKED</span>
              </span>
            )}
            <span className="text-xs text-slate-600">
              {data.overallStatus === 'VALID'
                ? 'All required COB source facts and validation gates are satisfied.'
                : `${data.blockingCount} blocking validation condition(s) prevent secondary packet assembly.`}
            </span>
          </div>

          <div className="flex items-center gap-2 font-mono text-[11px]">
            <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
              {data.validCount} Valid
            </span>
            <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
              {data.warningCount} Warnings
            </span>
            <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-800 border border-rose-200">
              {data.blockingCount} Blocking
            </span>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-2 border-b border-slate-200 pb-2 shrink-0">
          <button
            onClick={() => setActiveTab('facts')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
              activeTab === 'facts'
                ? 'bg-slate-800 text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:text-slate-900 border border-slate-200'
            }`}
          >
            1. Source Evidence & Adjudication Facts
          </button>
          <button
            onClick={() => setActiveTab('crosswalk')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
              activeTab === 'crosswalk'
                ? 'bg-slate-800 text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:text-slate-900 border border-slate-200'
            }`}
          >
            2. Future 837P COB Mapping Crosswalk
          </button>
          <button
            onClick={() => setActiveTab('validations')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors flex items-center gap-1.5 ${
              activeTab === 'validations'
                ? 'bg-slate-800 text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:text-slate-900 border border-slate-200'
            }`}
          >
            <span>3. Validation Rules Matrix</span>
            {data.blockingCount > 0 && (
              <span className="px-1.5 py-0.2 rounded-full bg-rose-600 text-white text-[10px] font-bold">
                {data.blockingCount}
              </span>
            )}
          </button>
        </div>

        {/* Tab Content Body (Scrollable) */}
        <div className="flex-1 overflow-y-auto pr-1 space-y-4 text-xs">
          {/* TAB 1: SOURCE EVIDENCE & ADJUDICATION FACTS */}
          {activeTab === 'facts' && (
            <div className="space-y-4">
              {/* Primary Claim Identity */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/90 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-1.5 uppercase text-[11px] tracking-wider">
                    <FileText className="w-3.5 h-3.5 text-slate-600" />
                    <span>Primary Commercial Claim Identity</span>
                  </h4>
                  <span className="font-mono text-[10px] text-slate-700 bg-slate-200/80 px-2 py-0.5 rounded border border-slate-300">
                    Route: {src.primaryClaimRoute || 'CLAIMMD_COMMERCIAL'}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
                  <div>
                    <span className="text-slate-500 block">Pulse Claim ID:</span>
                    <span className="font-mono font-semibold text-slate-800">{src.primaryClaimId || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Claim Number:</span>
                    <span className="font-mono font-semibold text-slate-800">{src.primaryClaimNumber || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Primary Payer:</span>
                    <span className="font-semibold text-slate-800">{src.primaryPayerName || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Electronic Payer ID:</span>
                    <span className="font-mono font-semibold text-emerald-700">
                      {norm?.primaryPayer.resolvedElectronicId || src.primaryPayerId || 'MISSING'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Primary Policy ID:</span>
                    <span className="font-mono font-semibold text-slate-800">{src.primaryPolicyId || 'MISSING'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Payer Control Number (ICN/CCN):</span>
                    <span className="font-mono font-semibold text-amber-700">
                      {src.payerControlNumber || 'MISSING'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Submission Date:</span>
                    <span className="text-slate-800">{src.primaryClaimCreatedAt?.slice(0, 10) || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Primary Adjudication Date:</span>
                    <span className="text-slate-800 font-mono font-semibold">{src.adjudicationDate || 'MISSING'}</span>
                  </div>
                </div>
              </div>

              {/* Linked Canonical EOB & Source Document */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/90 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-1.5 uppercase text-[11px] tracking-wider">
                    <ShieldCheck className="w-3.5 h-3.5 text-slate-600" />
                    <span>Canonical EOB & Document Verification</span>
                  </h4>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded border font-semibold ${
                    src.eobReviewStatus === 'VERIFIED'
                      ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                      : 'bg-amber-100 text-amber-800 border-amber-300'
                  }`}>
                    Review Status: {src.eobReviewStatus || 'UNLINKED'}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
                  <div>
                    <span className="text-slate-500 block">Canonical EOB ID:</span>
                    <span className="font-mono text-slate-800">{src.canonicalEobId || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Archive Filename:</span>
                    <span className="font-mono text-slate-800 truncate block" title={src.eobFilename}>{src.eobFilename || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Received Date:</span>
                    <span className="text-slate-800">{src.eobReceivedDate || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">EOB Total Amount:</span>
                    <span className="font-mono text-slate-800">${Number(src.eobTotalPaymentAmount || 0).toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Payment Header ID:</span>
                    <span className="font-mono text-slate-800">{src.paymentHeaderId || 'None'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Check / Trace #:</span>
                    <span className="font-mono text-slate-800">{src.checkEftNumber || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Payment Method:</span>
                    <span className="text-slate-800">{src.paymentMethod || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Check / EFT Date:</span>
                    <span className="text-slate-800">{src.paymentDate || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Financial Adjudication Breakdown */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/90 space-y-3">
                <h4 className="font-semibold text-slate-800 flex items-center gap-1.5 uppercase text-[11px] tracking-wider">
                  <DollarSign className="w-3.5 h-3.5 text-slate-600" />
                  <span>Financial Adjudication Values</span>
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-2.5">
                  <div className="p-2.5 rounded-lg bg-white border border-slate-200 text-center shadow-xs">
                    <span className="text-[10px] text-slate-500 block">Billed Charge</span>
                    <span className="font-mono text-sm font-bold text-slate-800">
                      ${Number(src.billedAmount ?? src.calculatedCharge ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="p-2.5 rounded-lg bg-white border border-slate-200 text-center shadow-xs">
                    <span className="text-[10px] text-slate-500 block">Allowed Amount</span>
                    <span className="font-mono text-sm font-bold text-slate-800">
                      ${Number(src.allowedAmount ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-center shadow-xs">
                    <span className="text-[10px] text-emerald-700 block">Primary Paid</span>
                    <span className="font-mono text-sm font-bold text-emerald-700">
                      ${Number(src.paidAmount ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="p-2.5 rounded-lg bg-amber-50/70 border border-amber-200 text-center shadow-xs">
                    <span className="text-[10px] text-amber-700 block">Contractual Adj (CO)</span>
                    <span className="font-mono text-sm font-bold text-amber-700">
                      ${Number(src.contractualAdjustment ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="p-2.5 rounded-lg bg-sky-50/70 border border-sky-200 text-center shadow-xs">
                    <span className="text-[10px] text-sky-700 block">Patient Resp (PR)</span>
                    <span className="font-mono text-sm font-bold text-sky-700">
                      ${Number(src.patientResponsibility ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="p-2.5 rounded-lg bg-slate-100 border border-slate-300 text-center shadow-xs">
                    <span className="text-[10px] text-slate-700 block">Secondary Balance</span>
                    <span className="font-mono text-sm font-bold text-slate-900">
                      ${Number(src.remainingSecondaryBalance ?? norm?.claimAdjudication?.remainingSecondaryAmount ?? 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Denial & Adjustment Evidence */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/90 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-1.5 uppercase text-[11px] tracking-wider">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                    <span>Adjustment & Denial Evidence Details</span>
                  </h4>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded border font-semibold ${
                    src.adjudicationOutcome === 'PAID'
                      ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                      : 'bg-amber-100 text-amber-800 border-amber-300'
                  }`}>
                    Outcome: {src.adjudicationOutcome || 'N/A'}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
                  <div>
                    <span className="text-slate-500 block">Payer Resp Group:</span>
                    <span className="font-mono font-semibold text-slate-800">{src.groupCode || 'PR'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">CARC Code:</span>
                    <span className="font-mono font-semibold text-amber-700">{src.carcCode || 'MISSING'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">RARC Code:</span>
                    <span className="font-mono font-semibold text-slate-800">{src.rarcCode || 'None'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">TPL Evidence Status:</span>
                    <span className="font-mono font-semibold text-slate-800">{src.tplEvidenceStatus || 'N/A'}</span>
                  </div>
                  <div className="col-span-2 sm:col-span-4">
                    <span className="text-slate-500 block">Plain-Language Denial / Reason Explanation:</span>
                    <span className="text-slate-700 italic">{src.denialReason || 'No descriptive text recorded.'}</span>
                  </div>
                </div>

                {norm?.claimAdjudication.isQualifyingZeroDollarDenial && (
                  <div className="p-2.5 rounded-lg bg-sky-50 border border-sky-200 flex items-center gap-2 text-sky-900 text-[11px]">
                    <CheckCircle2 className="w-4 h-4 text-sky-600 shrink-0" />
                    <span>
                      <strong>$0 Qualifying Denial Recognized:</strong> Primary commercial denial is supported by documented CARC ({src.carcCode}). Under AHCCCS / DDD TPL rules, this fulfills primary adjudication evidence for secondary Medicaid submission.
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 2: FUTURE 837P COB MAPPING CROSSWALK */}
          {activeTab === 'crosswalk' && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-700 flex items-center gap-2">
                <Info className="w-4 h-4 text-slate-600 shrink-0" />
                <span>
                  The mapping below displays the normalized COB concepts aligned to ANSI ASC X12 837P (005010X222A1) standards for secondary WellSky transmission. <strong>No 837 file is generated at this time.</strong>
                </span>
              </div>

              {norm ? (
                <div className="space-y-3.5">
                  {/* Loop 2000B / 2010BA / 2010BB */}
                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/90 space-y-2.5">
                    <div className="flex items-center justify-between border-b border-slate-200 pb-1.5">
                      <span className="font-mono text-slate-900 font-bold text-xs">Loop 2000B / 2010BA / 2010BB</span>
                      <span className="text-[10px] text-slate-500">Subscriber & Secondary Payer (Medicaid DDD)</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-[11px] font-mono">
                      <div>
                        <span className="text-slate-500 text-[10px] block">SBR01 / SBR09</span>
                        <span className="text-slate-800">SBR*S*18*******MC~</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] block">NM1*IL (Member ID)</span>
                        <span className="text-emerald-700">MI*{norm.secondarySubscriber.memberAhcccsId}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] block">NM1*PR (Secondary Payer)</span>
                        <span className="text-slate-800">AZDESDDD (PI*866004791)</span>
                      </div>
                    </div>
                  </div>

                  {/* Loop 2300 */}
                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/90 space-y-2.5">
                    <div className="flex items-center justify-between border-b border-slate-200 pb-1.5">
                      <span className="font-mono text-slate-900 font-bold text-xs">Loop 2300</span>
                      <span className="text-[10px] text-slate-500">Secondary Claim Header</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-[11px] font-mono">
                      <div>
                        <span className="text-slate-500 text-[10px] block">CLM01 / CLM02</span>
                        <span className="text-slate-800">CLM*{norm.secondaryClaimHeader.suggestedClaimNumber}*${norm.secondaryClaimHeader.claimBilledAmount.toFixed(2)}~</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] block">REF*G1 (Prior Auth)</span>
                        <span className="text-slate-800">REF*G1*{norm.secondaryClaimHeader.authNumber || 'MISSING'}~</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] block">HI*BK (Diagnosis)</span>
                        <span className="text-slate-800">HI*BK:{norm.secondaryClaimHeader.diagnosisCode || 'MISSING'}~</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] block">CLM05-1 (POS)</span>
                        <span className="text-slate-800">{norm.secondaryClaimHeader.placeOfService || '11'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Loop 2310B */}
                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/90 space-y-2.5">
                    <div className="flex items-center justify-between border-b border-slate-200 pb-1.5">
                      <span className="font-mono text-slate-900 font-bold text-xs">Loop 2310B</span>
                      <span className="text-[10px] text-slate-500">Rendering Provider</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-[11px] font-mono">
                      <div>
                        <span className="text-slate-500 text-[10px] block">NM1*82 (Provider Name & NPI)</span>
                        <span className="text-slate-800">{norm.renderingProvider.providerName} (XX*{norm.renderingProvider.npi})</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] block">REF*G2 (AHCCCS ID)</span>
                        <span className="text-emerald-700">REF*G2*{norm.renderingProvider.ahcccsId || 'MISSING'}~</span>
                      </div>
                    </div>
                  </div>

                  {/* Loop 2320 / 2330A / 2330B */}
                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/90 space-y-2.5">
                    <div className="flex items-center justify-between border-b border-slate-200 pb-1.5">
                      <span className="font-mono text-slate-900 font-bold text-xs">Loop 2320 / 2330A / 2330B</span>
                      <span className="text-[10px] text-slate-500">Other Subscriber & Other Payer (COB Primary Adjudication)</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-[11px] font-mono">
                      <div>
                        <span className="text-slate-500 text-[10px] block">SBR*P (Primary Commercial)</span>
                        <span className="text-slate-800">SBR*P*18*******CI~</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] block">CAS (Claim Adjustment)</span>
                        <span className="text-amber-700">
                          CAS*{norm.claimAdjudication.claimAdjustmentGroupCode}*{norm.claimAdjudication.claimAdjustmentReasonCode}*${Number(norm.claimAdjudication.contractualAdjustment || norm.claimAdjudication.patientResponsibility || 0).toFixed(2)}~
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] block">AMT*D (Primary Paid)</span>
                        <span className="text-emerald-700">AMT*D*{norm.claimAdjudication.paidAmount.toFixed(2)}~</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] block">OI***Y***Y (Other Ins Info)</span>
                        <span className="text-slate-800">OI***Y***Y~</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] block">NM1*IL (2330A Other Subscriber)</span>
                        <span className="text-slate-800">MI*{norm.otherSubscriber.primaryPolicyId}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] block">NM1*PR (2330B Primary Payer)</span>
                        <span className="text-slate-800">{norm.primaryPayer.name} (PI*{norm.primaryPayer.resolvedElectronicId})</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] block">DTP*573 (Adjudication Date)</span>
                        <span className="text-slate-800">DTP*573*D8*{norm.claimAdjudication.adjudicationDate}~</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] block">REF*F8 (Payer Claim Control #)</span>
                        <span className="text-amber-700">
                          {norm.claimAdjudication.payerControlNumber ? `REF*F8*${norm.claimAdjudication.payerControlNumber}~` : 'MISSING (REF*F8)'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Loop 2400 / 2430 */}
                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/90 space-y-2.5">
                    <div className="flex items-center justify-between border-b border-slate-200 pb-1.5">
                      <span className="font-mono text-slate-900 font-bold text-xs">Loop 2400 / 2430</span>
                      <span className="text-[10px] text-slate-500">Service Line & Line Adjudication Information</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-[11px] font-mono">
                      <div>
                        <span className="text-slate-500 text-[10px] block">SV1 (Service Line)</span>
                        <span className="text-slate-800">SV1*HC:{norm.lineCob.serviceCode}*${norm.lineCob.billedAmount.toFixed(2)}*UN*{norm.lineCob.units}~</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] block">SVD (Line Adjudication)</span>
                        <span className="text-slate-800">SVD*{norm.lineCob.primaryPayerId}*${norm.lineCob.primaryPaidAmount.toFixed(2)}*HC:{norm.lineCob.serviceCode}~</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] block">CAS (Line Adjustment)</span>
                        <span className="text-amber-700">
                          CAS*{norm.lineCob.lineAdjustmentGroupCode}*{norm.lineCob.lineAdjustmentReasonCode}*${norm.lineCob.lineAdjustmentAmount.toFixed(2)}~
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center text-slate-500 rounded-xl bg-slate-50 border border-slate-200">
                  Normalized 837P mapping unavailable due to missing or unverified primary adjudication facts.
                </div>
              )}
            </div>
          )}

          {/* TAB 3: VALIDATION MATRIX */}
          {activeTab === 'validations' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between pb-1">
                <div className="text-xs text-slate-800 font-semibold">
                  Rule Evaluation Matrix ({filteredValidations.length} of {validations.length})
                </div>
                <div className="flex items-center gap-1.5 text-[11px]">
                  <span className="text-slate-500 mr-1">Filter:</span>
                  {(['ALL', 'BLOCKING', 'WARNING', 'VALID'] as const).map(sev => (
                    <button
                      key={sev}
                      onClick={() => setFilterSeverity(sev)}
                      className={`px-2 py-0.5 rounded cursor-pointer transition-colors ${
                        filterSeverity === sev
                          ? 'bg-slate-800 text-white font-bold'
                          : 'bg-slate-100 text-slate-600 hover:text-slate-900 border border-slate-200'
                      }`}
                    >
                      {sev}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100 bg-white">
                {filteredValidations.map((v, idx) => (
                  <div
                    key={idx}
                    className={`p-3 text-xs flex items-start gap-3 ${
                      v.severity === 'BLOCKING'
                        ? 'bg-rose-50/50 hover:bg-rose-50'
                        : v.severity === 'WARNING'
                        ? 'bg-amber-50/40 hover:bg-amber-50'
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    <span className={`shrink-0 text-[10px] font-mono px-2 py-0.5 rounded font-bold uppercase ${
                      v.severity === 'BLOCKING'
                        ? 'bg-rose-100 text-rose-800 border border-rose-300'
                        : v.severity === 'WARNING'
                        ? 'bg-amber-100 text-amber-800 border border-amber-300'
                        : 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                    }`}>
                      {v.severity}
                    </span>

                    <div className="flex-1 min-w-0 space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-slate-800 font-semibold text-[11px]">{v.code}</span>
                        <span className="text-[10px] text-slate-500">({v.field})</span>
                      </div>
                      <div className={`text-[11px] ${
                        v.severity === 'BLOCKING' ? 'text-rose-900' : v.severity === 'WARNING' ? 'text-amber-900' : 'text-slate-700'
                      }`}>
                        {v.message}
                      </div>
                      {v.value !== undefined && v.value !== null && (
                        <div className="text-[10px] text-slate-500 font-mono pt-0.5">
                          Evaluated: <span className="text-slate-800">{typeof v.value === 'object' ? JSON.stringify(v.value) : String(v.value)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-3.5 border-t border-slate-200 shrink-0 text-xs text-slate-500">
          <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span>Strict No-Mutation: Viewing preview does not alter claim status, readiness, payment balances, or emit 837 EDI files.</span>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            {generateError && (
              <div className="text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1 max-w-md">
                {generateError}
              </div>
            )}
            {onGenerate && data.sourceFacts.readinessStatus === 'PRIMARY_ADJUDICATED_COB_REQUIRED' && (
              <button
                onClick={onGenerate}
                disabled={generating}
                className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold cursor-pointer transition-colors disabled:opacity-50"
              >
                {generating ? 'Generating...' : 'Generate DDD Secondary 837'}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 text-xs font-semibold cursor-pointer transition-colors"
            >
              Close Preview
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
